import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../../lib/content";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-[#E5E7EB] bg-white/90 backdrop-blur-xl"
          : "border-transparent bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-3"
        >
          <img
            src={BRAND.logoUrl}
            alt="Accrue Metrics"
            className="h-11 w-11 object-contain"
          />
          <div className="leading-tight">
            <div className="font-serif text-xl font-semibold tracking-tight text-[#0A1F44]">
              Accrue <span className="text-[#C9A227]">Metrics</span>
            </div>
            <div className="hidden text-[10px] uppercase tracking-[0.18em] text-[#475467] sm:block">
              Supporting Firms Behind the Scenes
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-10 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `gold-underline text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#0A1F44]" : "text-[#475467] hover:text-[#0A1F44]"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="navbar-cta-book-discovery"
            className="hover-lift inline-flex items-center gap-2 rounded-sm bg-[#0A1F44] px-5 py-3 text-sm font-semibold text-white hover:bg-[#071630]"
          >
            Book a Discovery Call
            <span className="text-[#C9A227]">→</span>
          </Link>
        </div>

        <button
          type="button"
          aria-label="Open menu"
          data-testid="navbar-mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-sm text-[#0A1F44] hover:bg-[#0A1F44]/5 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[#E5E7EB] bg-white lg:hidden" data-testid="navbar-mobile-panel">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-sm px-3 py-3 text-base font-medium ${
                    isActive ? "bg-[#0A1F44]/5 text-[#0A1F44]" : "text-[#475467] hover:bg-[#0A1F44]/5"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-navbar-cta"
              className="mt-3 inline-flex items-center justify-center rounded-sm bg-[#0A1F44] px-5 py-3 text-sm font-semibold text-white"
            >
              Book a Discovery Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
